/**
 * Command registry pattern for bulk action execution.
 *
 * Commands receive pre-filtered items from eligibility (no re-validation needed).
 * They map to IDs and dispatch to handlers or modals.
 * Registry makes adding new actions easy without modifying existing code.
 */
import i18n from 'modules/core/lib/i18n'
import app from 'modules/core/store/models/app'

import { useExpensesNavigation } from 'modules/expenses/hooks/useExpensesNavigation'
import {
  everyReimbursableCurrencyAllowed,
  showNotAllowedCurrenciesMessage,
} from 'modules/expenses/lib/expensables/expensable'

import { ACTION_IDS, ActionId } from './determineAvailableActions'
import { ActionEligibility, Expensable } from './types'

// Bulk actions exclude VIEW_GROUP which is only used for item-level actions
type BulkActionId = Exclude<ActionId, typeof ACTION_IDS.VIEW_GROUP>

interface ActionCommand {
  readonly id: BulkActionId
  execute(
    eligibility: ActionEligibility,
    handlers: ActionHandlers,
    modals: ModalHandlers
  ): Promise<void>
}

export interface ActionHandlers {
  onApprove: (ids: number[], overrideApprovalFlow?: boolean) => Promise<unknown>
  onReject: (ids: number[], reason?: string, overrideApprovalFlow?: boolean) => Promise<unknown>
  onSetToPaid: (ids: number[]) => Promise<unknown>
  onRestartReview: (ids: number[]) => Promise<unknown>
  onRemoveFromPayroll: (ids: number[]) => Promise<unknown>
  onRevertPaid: (ids: number[]) => Promise<unknown>
}

export interface ModalHandlers {
  openRejectModal: (expensables: Expensable[]) => void
  openPayrollModal: (expensables: Expensable[]) => void
  openPaymentModal: (expensables: Expensable[]) => void
}

export interface NavigationHandler {
  navigate: (path: string) => void
  navigation: ReturnType<typeof useExpensesNavigation>['navigation']
}

const showNoEligibleExpensesMessage = () => {
  app.setFlash({
    type: 'accent',
    message: i18n.t('expenses_v2.list.actions.no_actions_available'),
  })
}

class ApproveCommand implements ActionCommand {
  readonly id = ACTION_IDS.APPROVE

  async execute(eligibility: ActionEligibility, handlers: ActionHandlers): Promise<void> {
    const ids = eligibility.eligibleForApproval.map((exp) => exp.id)
    if (ids.length > 0) {
      await handlers.onApprove(ids)
    }
  }
}

class RejectCommand implements ActionCommand {
  readonly id = ACTION_IDS.REJECT

  async execute(
    eligibility: ActionEligibility,
    _handlers: ActionHandlers,
    modals: ModalHandlers
  ): Promise<void> {
    const expensables = eligibility.eligibleForRejection

    if (expensables.length === 0) {
      showNoEligibleExpensesMessage()
      return
    }

    modals.openRejectModal(expensables)
  }
}

class ReimbursePayrollCommand implements ActionCommand {
  readonly id = ACTION_IDS.REIMBURSE_PAYROLL

  async execute(
    eligibility: ActionEligibility,
    _handlers: ActionHandlers,
    modals: ModalHandlers
  ): Promise<void> {
    const expensables = eligibility.eligibleForPayroll

    if (expensables.length === 0) {
      showNoEligibleExpensesMessage()
      return
    }

    if (!everyReimbursableCurrencyAllowed(expensables)) {
      showNotAllowedCurrenciesMessage()
      return
    }

    modals.openPayrollModal(expensables)
  }
}

class ReimburseSepaCommand implements ActionCommand {
  readonly id = ACTION_IDS.REIMBURSE_SEPA

  async execute(
    eligibility: ActionEligibility,
    _handlers: ActionHandlers,
    modals: ModalHandlers
  ): Promise<void> {
    const expensables = eligibility.eligibleForSepa

    if (expensables.length === 0) {
      showNoEligibleExpensesMessage()
      return
    }

    modals.openPaymentModal(expensables)
  }
}

class SetPaidCommand implements ActionCommand {
  readonly id = ACTION_IDS.SET_PAID

  async execute(eligibility: ActionEligibility, handlers: ActionHandlers): Promise<void> {
    const ids = eligibility.eligibleForSetPaid.map((exp) => exp.id)
    if (ids.length > 0) {
      await handlers.onSetToPaid(ids)
    }
  }
}

class ResetApprovalCommand implements ActionCommand {
  readonly id = ACTION_IDS.RESET_APPROVAL

  async execute(eligibility: ActionEligibility, handlers: ActionHandlers): Promise<void> {
    const ids = eligibility.eligibleForResetApproval.map((exp) => exp.id)
    if (ids.length > 0) {
      await handlers.onRestartReview(ids)
    }
  }
}

class RemoveFromPayrollCommand implements ActionCommand {
  readonly id = ACTION_IDS.REMOVE_FROM_PAYROLL

  async execute(eligibility: ActionEligibility, handlers: ActionHandlers): Promise<void> {
    const ids = eligibility.eligibleForRemovePayroll.map((exp) => exp.id)
    if (ids.length > 0) {
      await handlers.onRemoveFromPayroll(ids)
    }
  }
}

class RevertPaidCommand implements ActionCommand {
  readonly id = ACTION_IDS.REVERT_PAID

  async execute(eligibility: ActionEligibility, handlers: ActionHandlers): Promise<void> {
    const ids = eligibility.eligibleForRevertPaid.map((exp) => exp.id)
    if (ids.length > 0) {
      await handlers.onRevertPaid(ids)
    }
  }
}

const commandRegistry: Record<BulkActionId, ActionCommand> = {
  [ACTION_IDS.APPROVE]: new ApproveCommand(),
  [ACTION_IDS.REJECT]: new RejectCommand(),
  [ACTION_IDS.REIMBURSE_PAYROLL]: new ReimbursePayrollCommand(),
  [ACTION_IDS.REIMBURSE_SEPA]: new ReimburseSepaCommand(),
  [ACTION_IDS.SET_PAID]: new SetPaidCommand(),
  [ACTION_IDS.RESET_APPROVAL]: new ResetApprovalCommand(),
  [ACTION_IDS.REMOVE_FROM_PAYROLL]: new RemoveFromPayrollCommand(),
  [ACTION_IDS.REVERT_PAID]: new RevertPaidCommand(),
}

const isValidActionId = (actionId: string): actionId is BulkActionId => {
  return actionId in commandRegistry
}

export const getCommand = (actionId: string): ActionCommand | undefined => {
  if (!isValidActionId(actionId)) {
    return undefined
  }
  return commandRegistry[actionId]
}
